import { Button, Input, Modal } from '@insify/design-system/components';
import { headingXxxs, textSm } from '@insify/design-system/typography';
import { useDebounce } from '@insify/react-hooks';
import { makeInputContext, makeInputValueContext } from '@objectiv/schema';
import { useInputChangeEventTracker } from '@objectiv/tracker-react';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { Search as SearchIcon } from 'react-feather';

import { ActivitiesList, ListWrapper } from './ActivitiesList';
import styles from './ActivitySelector.module.css';
import { Details } from './Details';
import { NoResult } from './NoResult';
import { Separator } from './Separator';
import { VerticalModal } from './VerticalModal';
import { ActivityProvider, actions, useActivity } from './useActivity';

import type { ProviderProps, SimplifiedActivity } from './types';
import type React from 'react';

export type Props = {
  className?: string;
  orderedVerticals: { name: string; icon: React.ReactElement }[];
  showInclusionsExclusions?: boolean; // For when these are handled on another part of the funnel
  activities: SimplifiedActivity[] | undefined;
  registrySuggestions?: string[];
  selected: string[];
  translations: {
    searchPlaceholder: string;
    yourActivities: string;
    suggested?: string;
    verticalHeading: string;
    searchActivities: string;
    verticalModalHeading: string;
    save: string;
    details: string;
    inclusions: string;
    exclusions: string;
    selectActivity: string;
    noResultHeader: string;
    noResultCTA?: string;
    noResultPhone?: string;
  };
};

const ActivitySelectorInner = ({
  className,
  orderedVerticals,
  activities = [],
  registrySuggestions,
  selected,
  showInclusionsExclusions = true,
  translations,
}: Props) => {
  const { state, dispatch } = useActivity();
  const [isActivityLoading, setIsActivityLoading] = useState(true);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const TRACKING_ID = 'old-activity-selector';

  const trackInputChange = useInputChangeEventTracker({
    locationStack: [makeInputContext({ id: TRACKING_ID })],
  });

  const debouncedSearchString = useDebounce(state.searchString, 200);

  useEffect(() => {
    if (debouncedSearchString) {
      void trackInputChange({
        globalContexts: [
          makeInputValueContext({
            id: TRACKING_ID,
            value: debouncedSearchString,
          }),
        ],
      });
    }
  }, [debouncedSearchString]);

  useEffect(() => {
    if (activities.length !== 0) {
      dispatch(actions.activitiesUpdated(activities));
      setIsActivityLoading(false);
    }
  }, [activities]);

  useEffect(() => {
    dispatch(actions.selectionUpdated(selected));
  }, [selected.join(';')]);

  useEffect(() => {
    if (registrySuggestions) {
      dispatch(actions.registrySuggestionsUpdated(registrySuggestions));
    }
  }, [registrySuggestions?.join(';')]);

  useEffect(() => {
    if (state.mode === 'search') {
      searchInputRef.current?.focus();
    }
  }, [state.mode]);

  if (isActivityLoading) {
    return null;
  }

  return (
    <>
      <Input
        className={className}
        ref={searchInputRef}
        error={state.error}
        name='_search'
        type='text'
        value={state.searchString}
        before={<SearchIcon />}
        placeholder={translations.searchPlaceholder}
        disabled={isActivityLoading}
        onFocus={() => dispatch(actions.searchFieldFocused())}
        onBlur={() => dispatch(actions.searchFieldBlurred())}
        onChange={(e) => {
          dispatch(actions.searchStringChanged(e.target.value));
        }}
        hasNoBottomContent={true}
        trackingId={TRACKING_ID}
      />
      {state.mode === 'search' ? (
        <>
          {!state.hasNoMatch && state.searched.length > 0 && (
            <ListWrapper>
              <ActivitiesList
                items={state.searched}
                translations={translations}
                onSelect={(id) => dispatch(actions.activitySelectedInSearch(id))}
                onDeselect={() => {}}
                showInclusionsExclusions={showInclusionsExclusions}
                onDetailsClick={(id) => {
                  dispatch(actions.detailsOpened(id));
                }}
              />
            </ListWrapper>
          )}
          {state.hasNoMatch ? (
            <NoResult
              noResultHeader={translations.noResultHeader}
              noResultCTA={translations.noResultCTA}
              noResultPhone={translations.noResultPhone}
            />
          ) : null}
        </>
      ) : null}
      {state.registrySuggested.length !== 0 || state.hasBeenSelected.length !== 0 ? (
        <ListWrapper>
          <div className={clsx(textSm, styles.yourActivitiesHeading)}>{translations.yourActivities}</div>
          <ActivitiesList
            items={state.registrySuggested.concat(
              state.hasBeenSelected.filter((id) => !state.registrySuggested.includes(id)),
            )}
            translations={translations}
            onSelect={(id) => dispatch(actions.activitySelectedInVertical(id))}
            onDeselect={(id) => dispatch(actions.activityDeselectedInVertical(id))}
            showInclusionsExclusions={showInclusionsExclusions}
            onDetailsClick={(id) => {
              dispatch(actions.detailsOpened(id));
            }}
          />
        </ListWrapper>
      ) : null}
      {state.mode === 'vertical' && (
        <>
          <Separator className={styles.separator}>{translations.verticalHeading}</Separator>
          {orderedVerticals.length === 1 ? (
            <ListWrapper data-testid='verticals'>
              <ActivitiesList
                items={state.allActivityIds}
                translations={translations}
                onSelect={(id) => dispatch(actions.activitySelectedInVertical(id))}
                onDeselect={(id) => dispatch(actions.activityDeselectedInVertical(id))}
                showInclusionsExclusions={showInclusionsExclusions}
                onDetailsClick={(id) => dispatch(actions.detailsOpened(id))}
              />
            </ListWrapper>
          ) : (
            <div data-testid='verticals' className={styles.verticals}>
              {orderedVerticals.map(({ name, icon }) => (
                <Button
                  kind='neutral'
                  trackingId={name}
                  type='button'
                  key={name}
                  className={clsx(headingXxxs, styles.vertical)}
                  leftIcon={icon}
                  onClick={() => dispatch(actions.verticalSelected(name))}
                >
                  <span className={headingXxxs}>{state.verticalById[name]?.heading}</span>
                </Button>
              ))}
            </div>
          )}
        </>
      )}
      <VerticalModal showInclusionsExclusions={showInclusionsExclusions} translations={translations} />
      <Modal
        trackingId='details-modal'
        open={state.verticalModalId === undefined && state.detailsActivityId !== undefined}
        onClose={() => dispatch(actions.detailsClosed())}
      >
        <Details translations={translations} onSelect={() => dispatch(actions.detailsClosed())} />
      </Modal>
    </>
  );
};

export const ActivitySelector = ({
  onAddActivity,
  onRemoveActivity,
  onSearchActivity,
  onSelectionChanged,
  error,
  ...restProps
}: Props & ProviderProps) => {
  return (
    <ActivityProvider
      error={error}
      onAddActivity={onAddActivity}
      onRemoveActivity={onRemoveActivity}
      onSearchActivity={onSearchActivity}
      onSelectionChanged={onSelectionChanged}
    >
      <ActivitySelectorInner {...restProps} />
    </ActivityProvider>
  );
};
